import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    Button, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, useMediaQuery, useTheme,
} from '@mui/material';
import PageContainer from '../../components/PageContainer';
import { AppDialog, EmptyState, FilterBar, PageHeader, Panel, StatusChip, useFilterBarStyles } from '../../components/ui';
import CustomerHistoryDialog, { CustomerSummary } from '../../components/CustomerHistoryDialog';
import { MockupsCustomers, Notifications } from '../../store/slices';
import { IStore } from '../../store/types';
import { customerUpdate } from '../../api';
import { useCan } from '../../auth/useCan';
import { MOCKUP_RW } from '../../auth/requirements';
import { mediaQuery } from '../../theme';
import { formatDate } from '../../lib/formatDate';
import { digitsOnly } from '../../lib/orderFilters';
import { errorMessage } from '../../lib/errorMessage';
import { asFlag, percentOf } from '../../lib/mockupEvents';
import { useStyles } from './styles';

const ROWS_PER_PAGE = [50, 100, 200, 500];
const DEFAULT_LIMIT = 50;

type SortKey = 'customerId' | 'createdAt' | 'eventCount' | 'addedToCartCount' | 'orderedCount' | 'lastEventDate' | 'ipCount';

interface Customer extends CustomerSummary {
    customerId: number;
    createdAt: string;
    eventCount: number;
    addedToCartCount: number;
    orderedCount: number;
    ipCount: number;
    lastEventDate: string;
    lastUpdated?: string;
    isBlocked: number | boolean;
}

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
    { key: 'customerId', label: 'Customer' },
    { key: 'eventCount', label: 'Uploads', numeric: true },
    { key: 'addedToCartCount', label: 'In cart', numeric: true },
    { key: 'orderedCount', label: 'Orders', numeric: true },
    { key: 'lastEventDate', label: 'Last upload' },
    { key: 'createdAt', label: 'First upload' },
    { key: 'ipCount', label: 'IPs', numeric: true },
];

const num = (value: number | null | undefined) => (value === null || value === undefined ? '—' : Number(value).toLocaleString('en-US'));

/**
 * `/mockup/customers`: everyone who uploaded a photo, served in order of total uploads (the API ignores
 * sort / search parameters, so column sorting is applied to the loaded page only). Any customer can be
 * looked up by id through the history dialog.
 */
const MockupsCustomersPage = () => {
    const { customers, status, queryParams } = useSelector<IStore, { customers: any; status: string; queryParams: any }>((state) => ({
        customers: state.mockupsCustomers.data,
        status: state.mockupsCustomers.status,
        queryParams: state.mockupsCustomers.queryParams,
    }));
    const dispatch = useDispatch();
    const classes = useStyles();
    const filterClasses = useFilterBarStyles();
    const theme = useTheme();
    const phone = useMediaQuery(mediaQuery(theme.custom.mq.sm));
    const canManage = useCan(MOCKUP_RW);
    const [searchParams, setSearchParams] = useSearchParams();
    const historyId = searchParams.get('customerId');
    const [findId, setFindId] = useState('');
    const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
    const [blockTarget, setBlockTarget] = useState<Customer | null>(null);
    const [blockPending, setBlockPending] = useState(false);

    const load = (params: { page: number; limit: number }) => dispatch<any>(MockupsCustomers.getMockupsCustomers(params));

    useEffect(() => {
        load({ page: queryParams?.page || 1, limit: queryParams?.limit || DEFAULT_LIMIT });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- first load only
    }, []);

    const rows = useMemo<Customer[]>(() => (Array.isArray(customers) ? customers : []), [customers]);
    const loading = status === 'loading';
    const failed = status === 'rejected';
    const page = Number(queryParams?.page) || 1;
    const limit = Number(queryParams?.limit) || DEFAULT_LIMIT;

    const sorted = useMemo(() => {
        if (!sort) return rows;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = a[sort.key];
            const bv = b[sort.key];
            if (av === null || av === undefined) return 1;
            if (bv === null || bv === undefined) return -1;
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [rows, sort]);

    const toggleSort = (key: SortKey) =>
        setSort((prev) => (prev?.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));

    const openHistory = (customerId: string | number) => setSearchParams({ customerId: String(customerId) }, { replace: true });
    const closeHistory = () => setSearchParams({}, { replace: true });
    const summary = useMemo(() => rows.find((row) => String(row.customerId) === historyId) || null, [rows, historyId]);
    const updatedAt = rows[0]?.lastUpdated;

    const confirmBlock = async () => {
        if (!blockTarget || blockPending) return;
        const next = !asFlag(blockTarget.isBlocked);
        setBlockPending(true);
        try {
            await customerUpdate({ data: { customerId: blockTarget.customerId, isBlocked: next }, onSuccess: undefined });
            // The flag flips only after the server accepted the change.
            dispatch(MockupsCustomers.updateCustomerStatus({ customerId: blockTarget.customerId, isBlocked: next ? 1 : 0 }));
            dispatch(Notifications.setNotifications({ text: next ? 'Customer blocked' : 'Customer unblocked', type: 'success' }));
            setBlockTarget(null);
        } catch (error) {
            dispatch(Notifications.setNotifications({ text: errorMessage(error), type: 'error' }));
        } finally {
            setBlockPending(false);
        }
    };

    const blockButton = (customer: Customer) =>
        canManage ? (
            <Button size="small" variant="text" color={asFlag(customer.isBlocked) ? 'primary' : 'error'} onClick={() => setBlockTarget(customer)}>
                {asFlag(customer.isBlocked) ? 'Unblock' : 'Block'}
            </Button>
        ) : null;

    const statusChip = (customer: Customer) =>
        asFlag(customer.isBlocked) ? <StatusChip tone="danger" size="sm">Blocked</StatusChip> : <StatusChip tone="success" size="sm">Active</StatusChip>;

    const pagination = (
        <TablePagination
            component="div"
            rowsPerPageOptions={ROWS_PER_PAGE}
            count={-1}
            rowsPerPage={limit}
            page={Math.max(0, page - 1)}
            onPageChange={(_, newPage) => load({ page: newPage + 1, limit })}
            onRowsPerPageChange={(event) => load({ page: 1, limit: Number(event.target.value) })}
            labelRowsPerPage={phone ? 'Rows' : 'Rows per page'}
            labelDisplayedRows={({ from, to }) => `${from}–${to}`}
            nextIconButtonProps={{ disabled: loading || rows.length < limit }}
            backIconButtonProps={{ disabled: loading || page <= 1 }}
        />
    );

    let body: React.ReactNode;
    if (loading) {
        body = <Skeleton animation="wave" width="100%" height={420} variant="rectangular" />;
    } else if (failed) {
        body = <EmptyState title="The customers could not be loaded" hint="Check the connection and try again." action={<Button variant="outlined" onClick={() => load({ page, limit })}>Retry</Button>} />;
    } else if (rows.length === 0) {
        body = (
            <EmptyState
                title="No customers on this page"
                hint="The list ended on the previous page."
                action={<Button variant="outlined" onClick={() => load({ page: 1, limit })}>First page</Button>}
            />
        );
    } else if (phone) {
        body = (
            <div className={classes.cards}>
                {sorted.map((customer) => (
                    <div key={customer.customerId} className={classes.card}>
                        <div className={classes.cardTop}>
                            <button type="button" className={classes.idButton} onClick={() => openHistory(customer.customerId)}>
                                <span className={classes.cardId}>{customer.customerId}</span>
                            </button>
                            {statusChip(customer)}
                        </div>
                        <div className={classes.cardGrid}>
                            <div><span className={classes.cardLabel}>Uploads</span><span className={classes.cardValue}>{num(customer.eventCount)}</span></div>
                            <div><span className={classes.cardLabel}>In cart</span><span className={classes.cardValue}>{num(customer.addedToCartCount)} · {percentOf(customer.addedToCartCount, customer.eventCount)}</span></div>
                            <div><span className={classes.cardLabel}>Orders</span><span className={classes.cardValue}>{num(customer.orderedCount)}</span></div>
                            <div><span className={classes.cardLabel}>Last upload</span><span className={classes.cardValue}>{formatDate(customer.lastEventDate, { time: true })}</span></div>
                            <div><span className={classes.cardLabel}>First upload</span><span className={classes.cardValue}>{formatDate(customer.createdAt)}</span></div>
                            <div><span className={classes.cardLabel}>IPs</span><span className={classes.cardValue}>{num(customer.ipCount)}</span></div>
                        </div>
                        <div className={classes.cardActions}>
                            {blockButton(customer)}
                            <Button size="small" variant="outlined" onClick={() => openHistory(customer.customerId)}>History</Button>
                        </div>
                    </div>
                ))}
            </div>
        );
    } else {
        body = (
            <TableContainer className={classes.container}>
                <Table stickyHeader size="small" aria-label="Customers">
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((column) => (
                                <TableCell key={column.key} align={column.numeric ? 'right' : 'left'} sortDirection={sort?.key === column.key ? sort.dir : false}>
                                    <TableSortLabel active={sort?.key === column.key} direction={sort?.key === column.key ? sort.dir : 'desc'} onClick={() => toggleSort(column.key)}>
                                        {column.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((customer) => (
                            <TableRow key={customer.customerId} hover>
                                <TableCell>
                                    <button type="button" className={classes.idButton} onClick={() => openHistory(customer.customerId)} aria-label={`Customer ${customer.customerId}: open upload history`}>
                                        {customer.customerId}
                                    </button>
                                </TableCell>
                                <TableCell className={classes.numeric}>{num(customer.eventCount)}</TableCell>
                                <TableCell className={classes.numeric}>
                                    {num(customer.addedToCartCount)}
                                    <span className={classes.rate}>{percentOf(customer.addedToCartCount, customer.eventCount)} of uploads</span>
                                </TableCell>
                                <TableCell className={classes.numeric}>
                                    {num(customer.orderedCount)}
                                    <span className={classes.rate}>{percentOf(customer.orderedCount, customer.addedToCartCount)} of cart</span>
                                </TableCell>
                                <TableCell className={classes.date}>{formatDate(customer.lastEventDate, { time: true })}</TableCell>
                                <TableCell className={classes.date}>{formatDate(customer.createdAt)}</TableCell>
                                <TableCell className={classes.numeric}>{num(customer.ipCount)}</TableCell>
                                <TableCell>{statusChip(customer)}</TableCell>
                                <TableCell>
                                    <div className={classes.actions}>
                                        {blockButton(customer)}
                                        <Button size="small" variant="outlined" onClick={() => openHistory(customer.customerId)}>History</Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    const lastBlockTarget = useRef<Customer | null>(null);
    if (blockTarget) lastBlockTarget.current = blockTarget;
    const blockShown = blockTarget ?? lastBlockTarget.current;
    const blocking = blockShown ? !asFlag(blockShown.isBlocked) : false;

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Mockups"
                title="Customers"
                subtitle="Everyone who uploaded a photo, with their uploads, carts and orders. Blocking stops new uploads."
            />
            <Panel
                padding="none"
                title="Customers"
                subtitle={updatedAt ? `Stats updated ${formatDate(updatedAt, { time: true })} · ordered by uploads` : 'Ordered by uploads'}
                footer={!loading && !failed && (rows.length > 0 || page > 1) ? pagination : undefined}
            >
                <FilterBar label="Find a customer">
                    <TextField
                        className={filterClasses.search}
                        size="small"
                        label="Customer ID"
                        value={findId}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        onChange={(event) => setFindId(digitsOnly(event.target.value))}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && findId) openHistory(findId);
                        }}
                    />
                    <Button variant="outlined" size="small" disabled={!findId} onClick={() => openHistory(findId)}>
                        Open history
                    </Button>
                    <p className={classes.note}>Any customer can be opened by id; column sorting applies to the loaded page.</p>
                </FilterBar>
                {body}
            </Panel>
            <CustomerHistoryDialog open={!!historyId} customerId={historyId} onClose={closeHistory} summary={summary} />
            <AppDialog
                open={!!blockTarget}
                onClose={() => setBlockTarget(null)}
                pending={blockPending}
                title={blockShown ? `${blocking ? 'Block' : 'Unblock'} customer ${blockShown.customerId}` : ''}
                description={blocking ? 'The customer will not be able to upload new photos. Existing mockups stay.' : 'The customer will be able to upload photos again.'}
                actions={(
                    <>
                        <Button variant="text" onClick={() => setBlockTarget(null)} disabled={blockPending}>Cancel</Button>
                        <Button variant="contained" color={blocking ? 'error' : 'primary'} onClick={confirmBlock} disabled={blockPending}>
                            {blocking ? 'Block' : 'Unblock'}
                        </Button>
                    </>
                )}
            />
        </PageContainer>
    );
};

export default MockupsCustomersPage;
